import { Button, Card } from "@prisma/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, Check, ChevronRight, Clapperboard, FileText, PackageCheck, Sparkles, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";

import { FulfilmentNote } from "../packages/PackagePage";
import { QueryState } from "../../components/QueryState";
import { RequestStatusChip } from "../../components/Status";
import { api, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { formatDate } from "../../lib/format";
import { useSession } from "../../lib/session";

type Overview = components["schemas"]["ProjectOverviewOut"];
const STEPS: { key: string; icon: LucideIcon }[] = [
  { key: "strategy", icon: Sparkles },
  { key: "plan", icon: FileText },
  { key: "production", icon: Clapperboard },
  { key: "review", icon: BadgeCheck },
  { key: "package", icon: PackageCheck },
];
const OPEN_STRATEGY = "openStrategy";
const STEP_STATE = { done: "done", current: "current", upcoming: "upcoming" } as const;
const STEP_OF_STATUS: Record<string, number> = {
  APPROVED_FOR_STRATEGY: 0, STRATEGY_IN_PROGRESS: 0, CREATIVE_PLAN_READY: 1, APPROVED_FOR_PRODUCTION: 2,
  IN_PRODUCTION: 2, REVIEW: 3, APPROVED: 4, DELIVERED: 4,
};

/** Project overview (plan v2 /projects/:id): where the approved request stands, stage by stage. */
export function ProjectOverviewPage() {
  const { displayId = "" } = useParams();
  const { t, i18n } = useTranslation();
  const me = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["project", displayId],
    queryFn: () => call<Overview>(api.GET("/app/v1/projects/{display_id}", { params: { path: { display_id: displayId } } })) });
  const open = useMutation({
    mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/strategy/open", { params: { path: { display_id: displayId } } })),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["project", displayId] }); void navigate(`/projects/${displayId}/strategy`); },
  });
  return (
    <QueryState query={q}>
      {(p) => {
        const current = STEP_OF_STATUS[p.status] ?? 0;
        const hyps = p.strategy.hypotheses;
        return (
          <div className="space-y-6">
            <nav aria-label={t("requests.breadcrumb")} className="flex items-center gap-1.5 text-[13px] text-ink-2">
              <Link to="/projects" className="hover:text-ink">{t("nav.projects")}</Link>
              <ChevronRight aria-hidden="true" className="size-3.5" />
              <span className="text-ink">{p.display_id}</span>
            </nav>
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-[34px] font-semibold tracking-[-0.02em] text-ink">{p.product.name}</h1>
                  <RequestStatusChip status={p.status} />
                </div>
                <p className="mt-1 text-[15px] text-ink-2">
                  {p.display_id} · {t(`requests.objective_${p.objective.toLowerCase()}`)} · {t("requests.due_on", { date: formatDate(p.deadline, i18n.language, me.time_zone) })}
                </p>
                <p className="mt-1 text-[14px] text-ink-2">
                  {p.assigned_cp ? t("requests.assigned_to", { name: p.assigned_cp.name }) : t("requests.unassigned")}
                  {" · "}<Link className="text-violet-text underline" to={`/requests/${p.display_id}`}>{t("projects.view_request")}</Link>
                </p>
              </div>
              {p.allowed_actions.includes(OPEN_STRATEGY) ? (
                <Button variant="primary" onClick={() => open.mutate()} disabled={open.isPending}>
                  <Sparkles aria-hidden="true" className="size-4" />{t("projects.start_strategy")}
                </Button>
              ) : (
                <Link to={`/projects/${p.display_id}/strategy`}
                  className="inline-flex h-10 items-center gap-2 rounded-[var(--radius-control)] bg-[image:var(--gradient-primary-button)] px-4 text-sm font-medium text-[var(--text-on-accent)]">
                  <Sparkles aria-hidden="true" className="size-4" />{t("projects.open_strategy")}
                </Link>
              )}
            </div>
            <ol aria-label={t("projects.stages_label")} className="grid grid-cols-5 gap-3">
              {STEPS.map((s, i) => {
                const state = i < current ? STEP_STATE.done : i === current ? STEP_STATE.current : STEP_STATE.upcoming;
                const Icon = i < current ? Check : s.icon;
                return (
                  <li key={s.key} aria-current={state === "current" ? "step" : undefined}
                    className={`flex items-center gap-3 rounded-[var(--radius-card)] border p-4 ${state === "current" ? "border-[var(--accent-primary)] bg-[var(--accent-primary-soft)]" : "border-line bg-surface"}`}>
                    <Icon aria-hidden="true" className={`size-5 ${state === "upcoming" ? "text-ink-2" : "text-violet"}`} />
                    <div>
                      <div className="text-[14px] font-medium text-ink">{t(`projects.step_${s.key}`)}</div>
                      <div className="text-[12px] text-ink-2">{t(`projects.state_${state}`)}</div>
                    </div>
                  </li>
                );
              })}
            </ol>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Card className="p-6">
                <h2 className="flex items-center gap-2 text-[18px] font-semibold text-ink"><Sparkles aria-hidden="true" className="size-5 text-violet" />{t("projects.strategy_card")}</h2>
                <dl className="mt-4 grid grid-cols-3 gap-4">
                  {(["KEPT", "EXPLORE", "DISCARDED"] as const).map((s) => (
                    <div key={s}>
                      <dt className="text-[12px] uppercase tracking-wide text-ink-2">{t(`karya.hyp_status_${s.toLowerCase()}`)}</dt>
                      <dd className="mt-1 text-[26px] font-semibold text-ink">{hyps[s] ?? 0}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-4 text-[13px] text-ink-2">{t("projects.strategy_note")}</p>
              </Card>
              <Card className="p-6">
                <h2 className="text-[18px] font-semibold text-ink">{t("requests.col_deliverables")}</h2>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {p.deliverables.map((d, i) => (
                    <li key={i} className="rounded-full border border-line bg-elevated px-3 py-1 text-[13px] text-ink">
                      {t(`requests.deliverable_n_${d.type.toLowerCase()}`, { count: d.quantity })}
                    </li>
                  ))}
                </ul>
              </Card>
              <Card className="p-6">
                <h2 className="text-[18px] font-semibold text-ink">{t("projects.plan_card")}</h2>
                {p.plan ? (
                  <p className="mt-2 text-[14px] text-ink">
                    {t("plan.version_n", { n: Number(p.plan.version) })} · {t(`plan.status_${String(p.plan.status).toLowerCase()}`)}
                  </p>
                ) : (
                  <p className="mt-2 text-[14px] text-ink-2">{t("projects.plan_empty")}</p>
                )}
                <Link to={`/projects/${p.display_id}/plan`} className="mt-3 inline-block text-[13px] text-violet-text hover:underline">{t("projects.open_plan")}</Link>
              </Card>
              <Card className="p-6">
                <h2 className="text-[18px] font-semibold text-ink">{t("projects.jobs_card")}</h2>
                {p.jobs ? (
                  <p className="mt-2 text-[14px] text-ink">{t("projects.jobs_total", { count: Number(p.jobs.total) })}</p>
                ) : (
                  <p className="mt-2 text-[14px] text-ink-2">{t("projects.jobs_empty")}</p>
                )}
              </Card>
              <Card className="p-6">
                <h2 className="text-[18px] font-semibold text-ink">{t("projects.package_card")}</h2>
                {p.package ? (
                  <p className="mt-2 text-[14px] text-ink">{t("plan.version_n", { n: Number(p.package.version) })} · {t(`package.status_${String(p.package.status).toLowerCase()}`)}</p>
                ) : (
                  <p className="mt-2 text-[14px] text-ink-2">{t("projects.package_empty")}</p>
                )}
                <FulfilmentNote f={(p.fulfilment ?? null) as components["schemas"]["FulfilmentOut"] | null} />
                <Link to={`/projects/${p.display_id}/package`} className="mt-3 inline-block text-[13px] text-violet-text hover:underline">{t("projects.open_package")}</Link>
              </Card>
            </div>
          </div>
        );
      }}
    </QueryState>
  );
}
